import { useState } from 'react';
import { MostrarCodigo } from '../activacion/MostrarCodigo.js';
import { CajaDeIcono } from '../components/domain/CajaDeIcono.js';
import { Boton } from '../components/ui/Boton.js';
import { Bell, Smartphone } from '../components/ui/iconos.js';
import { PACIENTE } from '../copy/index.js';
import { Stepper, TituloDelPaso } from './Stepper.js';

type PasoPermisosProps = {
  onTerminar: (pushHabilitado: boolean) => void;
  enviando: boolean;
  error?: string | undefined;
  /** Inyectable: en tests no hay API de notificaciones del navegador. */
  pedirPermisoPush?: () => Promise<boolean>;
};

async function pedirPermisoDelNavegador(): Promise<boolean> {
  if (typeof Notification === 'undefined') return false;
  const resultado = await Notification.requestPermission();
  return resultado === 'granted';
}

/**
 * P-02d · notificaciones e instalación.
 *
 * Dos acciones con CTA propio y **un solo botón que cierra el flujo**. Que
 * "Empezar mi programa" sea el único camino de salida importa: activar
 * notificaciones o instalar la app son mejoras, no requisitos, y bloquear el
 * arranque por ellas perdería pacientes en el último paso.
 *
 * Instalar la PWA no se puede forzar ni verificar desde la app: se explica cómo
 * y se confía. Prometerle al backend que se instaló sería registrar algo que no
 * sabemos.
 *
 * E3.8.2, contra la referencia P-02d: dos tarjetas con caja de ícono y una acción
 * cada una. El botón teal es la acción secundaria; el primario navy cierra.
 *
 * **Debajo de cómo instalar, el código de activación** (INGRESO-2): en el
 * iPhone la app instalada no comparte la cookie con Safari, así que la primera
 * vez que se abre desde el ícono pide este código. Se muestra a pedido.
 */
export function PasoPermisos({
  onTerminar,
  enviando,
  error,
  pedirPermisoPush = pedirPermisoDelNavegador,
}: PasoPermisosProps) {
  const [push, setPush] = useState<boolean | null>(null);
  const [pidiendo, setPidiendo] = useState(false);

  async function activar() {
    setPidiendo(true);
    try {
      setPush(await pedirPermisoPush());
    } catch {
      setPush(false);
    } finally {
      setPidiendo(false);
    }
  }

  const tarjeta =
    'flex items-start gap-4 rounded-lg bg-card p-4 shadow-low outline-1 -outline-offset-1 outline-border';

  return (
    <section className="flex flex-col">
      <Stepper paso={4} />

      <div className="flex flex-col gap-5 px-5 py-6">
        <div>
          <TituloDelPaso>{PACIENTE.onboarding.permisos.titulo}</TituloDelPaso>
          <p className="m-0 mt-1 text-lg text-body">{PACIENTE.onboarding.permisos.presentacion}</p>
        </div>

        <div className={tarjeta}>
          <CajaDeIcono icono={Bell} tono="ambar" />
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <h2 className="m-0 text-lg leading-tight font-bold text-heading">
              {PACIENTE.onboarding.permisos.pushTitulo}
            </h2>
            <p className="m-0 text-base text-subtle">{PACIENTE.onboarding.permisos.pushDetalle}</p>
            {push === true ? (
              <p className="m-0 mt-2 text-md font-bold text-accent-strong" role="status">
                {PACIENTE.onboarding.permisos.pushActivadas}
              </p>
            ) : (
              <Boton
                cargando={pidiendo}
                className="mt-3"
                escala="chica"
                onClick={activar}
                variante="teal"
              >
                {push === false
                  ? PACIENTE.onboarding.permisos.pushReintentar
                  : PACIENTE.onboarding.permisos.pushActivar}
              </Boton>
            )}
            {push === false ? (
              <p className="m-0 mt-2 text-base text-subtle">
                {PACIENTE.onboarding.permisos.pushRechazadas}
              </p>
            ) : null}
          </div>
        </div>

        <div className={tarjeta}>
          <CajaDeIcono icono={Smartphone} tono="navy" />
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <h2 className="m-0 text-lg leading-tight font-bold text-heading">
              {PACIENTE.onboarding.permisos.instalarTitulo}
            </h2>
            <p className="m-0 text-base text-subtle">
              {PACIENTE.onboarding.permisos.instalarDetalle}
            </p>
          </div>
        </div>

        <MostrarCodigo />

        {error ? (
          <p className="m-0 text-md font-bold text-destructive-fg" role="alert">
            {error}
          </p>
        ) : null}

        <Boton cargando={enviando} onClick={() => onTerminar(push === true)}>
          {PACIENTE.onboarding.permisos.terminar}
        </Boton>
      </div>
    </section>
  );
}
